// Row contracts of the operating-cadence rows (MEETINGS; 02 §Operating cadence; 06 §Cadence):
// issues, meeting series, meetings, agenda items, meeting outcomes. Same conventions as
// `rows.ts`: keys are column names, nullability is the column's, enums are the CHECK's;
// `contract-drift.test.ts` binds each to its table.
import { z } from "zod";
import { ActorShortColumns } from "./actor";
import {
  AgendaOutcomeKind,
  AgendaSection,
  AgendaSourceKind,
  IssueEscalation,
  IssueResolutionKind,
  IssueSourceKind,
  IssueStatus,
  MeetingOutcomeKind,
  MeetingPrereadState,
  MeetingSeriesKind,
  MeetingState,
} from "./enums";
import { Timestamp, Uuid } from "./primitives";

const actor = ActorShortColumns.shape;
const stamps = { created_at: Timestamp, updated_at: Timestamp };

/** A thing an issue's resolution made. */
export const IssueResolutionRef = z
  .strictObject({ kind: z.enum(["task", "decision"]), id: Uuid })
  .meta({ id: "IssueResolutionRef" });
export type IssueResolutionRef = z.infer<typeof IssueResolutionRef>;

export const Issue = z
  .object({
    company_id: Uuid,
    id: Uuid,
    team_id: Uuid.nullable(),
    owner_seat_id: Uuid.nullable(),
    raised_by_membership_id: Uuid.nullable(),
    title: z.string(),
    body: z.string().nullable(),
    priority: z.int().min(1).max(3),
    status: IssueStatus,
    position: z.int(),
    resolution_kind: IssueResolutionKind.nullable(),
    resolution_refs: z.array(IssueResolutionRef),
    resolution_note: z.string().nullable(),
    escalated_to: IssueEscalation,
    escalation_ref: z.string().nullable(),
    parent_issue_id: Uuid.nullable(),
    thread_id: Uuid.nullable(),
    source_kind: IssueSourceKind,
    source_id: Uuid.nullable(),
    source_label: z.string().nullable(),
    resolved_at: Timestamp.nullable(),
    archived_at: Timestamp.nullable(),
    ...actor,
    ...stamps,
  })
  .meta({ id: "Issue" });
export type Issue = z.infer<typeof Issue>;

/** Seconds a section of the agenda is given. */
const TimeBox = z.int().min(0).max(36000).optional();

/** A series' agenda configuration: the time box per section (seconds) and standing custom items. */
export const AgendaConfig = z
  .strictObject({
    time_boxes: z
      .strictObject({
        scorecard: TimeBox,
        priorities: TimeBox,
        issues: TimeBox,
        todos: TimeBox,
        headlines: TimeBox,
        custom: TimeBox,
      })
      .optional(),
    custom: z.array(z.string().min(1).max(500)).max(20).optional(),
  })
  .meta({ id: "AgendaConfig" });
export type AgendaConfig = z.infer<typeof AgendaConfig>;

export const MeetingSeries = z
  .object({
    company_id: Uuid,
    id: Uuid,
    team_id: Uuid.nullable(),
    kind: MeetingSeriesKind,
    name: z.string(),
    schedule: z.string(),
    duration_minutes: z.int(),
    attendees: z.array(Uuid),
    facilitator_seat_id: Uuid.nullable(),
    agenda_config: AgendaConfig,
    channel_id: Uuid.nullable(),
    board_id: Uuid.nullable(),
    preread_by_membership_id: Uuid.nullable(),
    archived_at: Timestamp.nullable(),
    ...actor,
    ...stamps,
  })
  .meta({ id: "MeetingSeries" });
export type MeetingSeries = z.infer<typeof MeetingSeries>;

export const Meeting = z
  .object({
    company_id: Uuid,
    id: Uuid,
    series_id: Uuid,
    scheduled_at: Timestamp,
    started_at: Timestamp.nullable(),
    ended_at: Timestamp.nullable(),
    state: MeetingState,
    notes: z.string(),
    rating: z.int().min(1).max(10).nullable(),
    thread_id: Uuid.nullable(),
    video_note_id: Uuid.nullable(),
    agenda_built_at: Timestamp.nullable(),
    current_item_id: Uuid.nullable(),
    item_started_at: Timestamp.nullable(),
    summary_message_id: Uuid.nullable(),
    preread_state: MeetingPrereadState,
    preread_wakeup_id: Uuid.nullable(),
    preread_requested_at: Timestamp.nullable(),
    preread_note: z.string().nullable(),
    ...actor,
    ...stamps,
  })
  .meta({ id: "Meeting" });
export type Meeting = z.infer<typeof Meeting>;

export const MeetingAgendaItem = z
  .object({
    company_id: Uuid,
    id: Uuid,
    meeting_id: Uuid,
    section: AgendaSection,
    source_kind: AgendaSourceKind,
    source_id: Uuid.nullable(),
    seat_id: Uuid.nullable(),
    title: z.string(),
    detail: z.string().nullable(),
    time_box_s: z.int(),
    elapsed_s: z.int(),
    position: z.int(),
    done_at: Timestamp.nullable(),
    outcome_kind: AgendaOutcomeKind,
    outcome_ref: Uuid.nullable(),
    ...actor,
    ...stamps,
  })
  .meta({ id: "MeetingAgendaItem" });
export type MeetingAgendaItem = z.infer<typeof MeetingAgendaItem>;

export const MeetingOutcome = z
  .object({
    company_id: Uuid,
    id: Uuid,
    meeting_id: Uuid,
    agenda_item_id: Uuid.nullable(),
    kind: MeetingOutcomeKind,
    ref_id: Uuid,
    title: z.string(),
    ...actor,
    created_at: Timestamp,
  })
  .meta({ id: "MeetingOutcome" });
export type MeetingOutcome = z.infer<typeof MeetingOutcome>;
